import { config, cognitoOAuthConfig } from '../config';
import type { AuthTokens } from '../types';

const TOKEN_KEY = 'auth_tokens';
const PKCE_VERIFIER_KEY = 'pkce_code_verifier';

function base64UrlEncode(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

async function generateCodeVerifier(): Promise<string> {
  const array = new Uint8Array(32);
  crypto.getRandomValues(array);
  return base64UrlEncode(array.buffer);
}

async function computeCodeChallenge(verifier: string): Promise<string> {
  const encoder = new TextEncoder();
  const data = encoder.encode(verifier);
  const digest = await crypto.subtle.digest('SHA-256', data);
  return base64UrlEncode(digest);
}

export const cognitoHelpers = {
  async getLoginUrl(): Promise<string> {
    const verifier = await generateCodeVerifier();
    const challenge = await computeCodeChallenge(verifier);
    sessionStorage.setItem(PKCE_VERIFIER_KEY, verifier);

    const params = new URLSearchParams({
      client_id: config.cognito.clientId,
      response_type: cognitoOAuthConfig.responseType,
      scope: cognitoOAuthConfig.scope.join(' '),
      redirect_uri: cognitoOAuthConfig.redirectSignIn,
      code_challenge: challenge,
      code_challenge_method: 'S256',
    });
    return `https://${config.cognito.domain}/oauth2/authorize?${params.toString()}`;
  },

  getLogoutUrl(): string {
    const params = new URLSearchParams({
      client_id: config.cognito.clientId,
      logout_uri: cognitoOAuthConfig.redirectSignOut,
    });
    return `https://${config.cognito.domain}/logout?${params.toString()}`;
  },

  async exchangeCodeForTokens(code: string): Promise<AuthTokens> {
    const params = new URLSearchParams({
      grant_type: 'authorization_code',
      client_id: config.cognito.clientId,
      code,
      redirect_uri: cognitoOAuthConfig.redirectSignIn,
    });
    const codeVerifier = sessionStorage.getItem(PKCE_VERIFIER_KEY);
    if (codeVerifier) {
      params.set('code_verifier', codeVerifier);
      sessionStorage.removeItem(PKCE_VERIFIER_KEY);
    }

    const response = await fetch(`https://${config.cognito.domain}/oauth2/token`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: params.toString(),
    });

    if (!response.ok) {
      throw new Error('Failed to exchange code for tokens');
    }

    const data = await response.json();
    return {
      idToken: data.id_token,
      accessToken: data.access_token,
      refreshToken: data.refresh_token,
    };
  },

  saveTokens(tokens: AuthTokens): void {
    localStorage.setItem(TOKEN_KEY, JSON.stringify(tokens));
  },

  getTokens(): AuthTokens | null {
    try {
      const raw = localStorage.getItem(TOKEN_KEY);
      if (!raw) return null;
      const parsed = JSON.parse(raw) as Record<string, unknown>;
      if (!parsed || typeof parsed !== 'object' || !parsed.idToken || !parsed.accessToken) {
        return null;
      }
      return {
        idToken: String(parsed.idToken),
        accessToken: String(parsed.accessToken),
        refreshToken: parsed.refreshToken != null ? String(parsed.refreshToken) : undefined,
      };
    } catch {
      return null;
    }
  },

  clearTokens(): void {
    localStorage.removeItem(TOKEN_KEY);
  },

  parseJwt(token: string | null | undefined): any {
    if (token == null || typeof token !== 'string' || !token.includes('.')) {
      return null;
    }
    try {
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split('')
          .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
          .join('')
      );
      return JSON.parse(jsonPayload);
    } catch {
      return null;
    }
  },

  isTokenExpired(token: string | null | undefined): boolean {
    const payload = this.parseJwt(token);
    if (!payload || typeof payload.exp !== 'number') return true;
    return Date.now() >= payload.exp * 1000;
  },

  async refreshTokens(): Promise<AuthTokens | null> {
    const tokens = this.getTokens();
    if (!tokens?.refreshToken) {
      return null;
    }
    const params = new URLSearchParams({
      grant_type: 'refresh_token',
      client_id: config.cognito.clientId,
      refresh_token: tokens.refreshToken,
    });
    const response = await fetch(`https://${config.cognito.domain}/oauth2/token`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: params.toString(),
    });
    if (!response.ok) {
      return null;
    }
    const data = (await response.json()) as Record<string, unknown>;
    if (!data.id_token || !data.access_token) {
      return null;
    }
    const next: AuthTokens = {
      idToken: String(data.id_token),
      accessToken: String(data.access_token),
      refreshToken:
        data.refresh_token != null ? String(data.refresh_token) : tokens.refreshToken,
    };
    this.saveTokens(next);
    return next;
  },
};
